import PropTypes from "prop-types";

import { cn } from "@helpers/general";
import { isOutOfStock } from "@helpers/product";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";

import useDiscountsInfo from "@hooks/useDiscountsInfo";
import { useCurrentCountryCode } from "@hooks/usePrices";
import useCheckInventory from "@hooks/useCheckInventory";

import { ComponentType } from "@constants/ComponentType";
import { Country } from "@constants/Country";
import { HandleType } from "@constants/HandleType";

import Customizable from "@images/custom.png";
/**
 * Tag order:
 * 1. Preorder
 * 2. Out of Stock
 * 3. Custom tag
 * 4. Solid Gold
 * 5. Final Sale
 * 6. Low In Stock
 * 7. Selling Fast (UTM)
 * 8. Gift Set
 * 9. New
 */

function getSaleTag(tags, countryCode) {
  const isUS = countryCode === Country.US;

  // Destructure the first element for fallback
  const [firstTag] = tags;

  // Pick US or RoW
  const selected =
    tags.find((t) => isUS && t.startsWith("Tag:US:")) ||
    tags.find((t) => !isUS && t.startsWith("Tag:RoW:")) ||
    firstTag; // fallback via destructured variable

  // Extract everything after the last ":"
  return selected.split(":").slice(-1)[0].trim();
}

function ALTag({
  product,
  componentType,
  shouldDisplayPreOrder,
  topOffset,
  // discountsInfo = [],
}) {
  const countryCode = useCurrentCountryCode();
  const discountsInfo = useDiscountsInfo();
  const discount = discountsInfo?.find((d) => d?.productSaleTag) || null;

  const { formattedDiscount, discountAmount, discount_given, compareAtPrice, isUtm } = product;

  const { handle, releaseDate, productDetails } = product?.contentful || {};
  const { tags, productType } = product?.node || {};

  const productInventory = useCheckInventory(handle);
  const isSoldOut = isOutOfStock(product.node, countryCode) && handle !== HandleType.GIFT_CARD;
  const isGiftCard = handle === "gift-card";

  const customTag = tags?.filter((e) => e.includes("Tag:"));
  const hasDiscount = formattedDiscount && discountAmount > 0;
  const isBundle = tags?.includes("bundle");
  const isCustomizable = tags?.includes("CUSTOMIZABLE");

  let tag = null;
  let type = null;
  let customColors = null;
  let customColorStyle = null;

  const currentDate = new Date();
  const timeDifference = currentDate - new Date(releaseDate);
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  const productDetailsComponents =
    productDetails?.components || productDetails?.componentsFilter || [];

  // 1. Preorder
  if (shouldDisplayPreOrder && countryCode === Country.US) {
    tag = "Preorder";
    type = "preorder";
    customColors = null;
  }

  // 2. Out of Stock | Sold Out
  else if (isSoldOut) {
    tag = "Sold Out";
    type = "outofstock";
    customColors = null;
  }

  // 3. Custom tag
  else if (customTag && customTag.length > 0) {
    tag = getSaleTag(customTag, countryCode);
    type = "custom";
    customColors = "bg-[#7797B5] text-white";
  }

  // 4. Solid Gold
  else if (
    tags?.includes("Components:Solid Gold") ||
    productDetailsComponents?.includes("Components:Solid Gold")
  ) {
    tag = "Solid Gold";
    type = "solidgold";
    customColors = null;
  }

  // 5. Final Sale
  else if (tags?.includes("Final Sale") && !hasDiscount) {
    tag = "Final Sale";
    type = "finalsale";
    customColors = null;
  }

  // 6. Product Discount Tag (only if product discount exists and we're on PDP or PLP)
  else if (hasDiscount && [ComponentType.PDP, ComponentType.CP].includes(componentType)) {
    if (tags?.includes("Final Sale") || compareAtPrice !== null) {
      tag = `${discount_given}% OFF`;
    }

    if (isBundle) {
      tag = `${formattedDiscount} OFF`;
    }

    type = "sale";
    customColors = null;
  }

  // 7. Low In Stock
  else if (productInventory.isLowInStock && !isGiftCard) {
    tag = "Low In Stock";
    type = "lowinstock";
    customColors = null;
  }

  // 8. Selling Fast (UTM)
  else if (isUtm) {
    tag = "Selling Fast";
    type = "sellingfast";
    customColors = null;
  }

  // 9. Gift Set
  else if (productType === "Jewelry Sets") {
    tag = "Gift Set";
    type = "default";
    customColors = null;
  }

  // 10. New
  else if (daysDifference <= 45 && daysDifference >= 0) {
    tag = "New";
    type = "new";
    customColors = null;
  }

  // 11. Sale tag
  else if (
    discount?.productSaleTag &&
    ((discount?.matchType === "exclude" && !discount?.matchTags.some((t) => tags?.includes(t))) ||
      (discount?.matchType === "include" && discount?.matchTags.some((t) => tags?.includes(t))))
  ) {
    tag = discount.productSaleTag.text;
    type = "custom";
    customColors =
      "bg-[var(--tag-bg-desktop)] text-[var(--tag-text-desktop)] mobile:bg-[var(--tag-bg-mobile)] mobile:text-[var(--tag-text-mobile)]";
    customColorStyle = {
      "--tag-bg-desktop": discount.productSaleTag.backgroundColor?.desktop || "#000",
      "--tag-text-desktop": discount.productSaleTag.textColor?.desktop || "#fff",
      "--tag-bg-mobile": discount.productSaleTag.backgroundColor?.mobile || "#000",
      "--tag-text-mobile": discount.productSaleTag.textColor?.mobile || "#fff",
    };
  }

  // 12. Customizable
  if (isCustomizable) {
    tag = "Customizable";
    type = "customizable";
    if (componentType === ComponentType.SIDE_CART) {
      tag = "Custom";
    }
  }
  // 13. MTO Card
  if (componentType === ComponentType.MTO_APP) {
    const customTagText = customTag?.length ? getSaleTag(customTag, countryCode) : null;
    tag = customTagText || "Customizable";
    type = customTagText ? "default" : "customizable";
    customColors = null;
    customColorStyle = null;
  }

  if (tag === null) {
    return null;
  }

  return (
    <div
      className={cn(
        "typo-micro-bold-caps px-[7px] py-[3px] absolute rounded-[4px] z-10",
        "top-[20px] left-[20px] mobile:left-[25px] mobile:top-[10px]",
        componentType === ComponentType.PDP && "top-[20px] mobile:left-[25px] mobile:top-[10px]",
        componentType === ComponentType.SIDE_CART &&
          "-top-[10px] left-1/2 mobile:left-1/2 mobile:-top-[10px] -translate-x-1/2 whitespace-nowrap border border-black",
        componentType === ComponentType.CP_OPTIONAL_PRODUCTS && "top-[12px] left-[15px]",
        (componentType === ComponentType.CP || componentType === ComponentType.MTO_APP) &&
          "top-[14px] left-[14px] mobile:top-[10px] mobile:left-[10px]",
        componentType === ComponentType.PRODUCT_RECOMMENDATIONS &&
          "top-[10px] left-[10px] mobile:top-[10px] mobile:left-[10px]",
        "bg-white text-primary", // default colors
        (type === "sale" || type === "finalsale" || type === "lowinstock") && "text-secondary",
        type === "solidgold" && "text-bronze",
        type === "custom" && "bg-[#7797B5] text-white",
        type === "outofstock" && "text-dark-grey",
        type === "customizable" && "flex items-center gap-[4px] mobile:gap-[3px]",
        type === "customizable" &&
          componentType === ComponentType.SIDE_CART &&
          "pr-[17px] mobile:pr-[15px]",
        customColors && customColors
      )}
      data-testid="al-tag"
      style={{
        top: `${topOffset + 13}px`,
        ...(customColorStyle || {}),
      }}
    >
      {type === "customizable" && (
        <img
          className="w-[11px] h-[11px] mobile:w-[10px] mobile:h-[10px]"
          src={Customizable}
          alt="Customizable"
          loading="lazy"
        />
      )}
      {tag}
    </div>
  );
}

ALTag.propTypes = {
  product: PropTypes.shape({
    formattedDiscount: PropTypes.string,
    discountAmount: PropTypes.number,
    discount_given: PropTypes.string,
    compareAtPrice: PropTypes.string,
    isUtm: PropTypes.bool,
    contentful: PropTypes.shape({
      handle: PropTypes.string,
      releaseDate: PropTypes.string,
      productDetails: PropTypes.shape({
        components: PropTypes.arrayOf(PropTypes.string),
        componentsFilter: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
    node: PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.string),
      productType: PropTypes.string,
    }),
  }).isRequired,
  componentType: PropTypes.string.isRequired,
  shouldDisplayPreOrder: PropTypes.bool,
  topOffset: PropTypes.number,
};

export default withALErrorBoundary({
  name: "ALTag",
  priority: "P3",
})(ALTag);
